import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, filter, map, Observable, switchMap, tap } from 'rxjs';
import { EmployeeProfile } from '@coin/shared/org-tree/util';
import { Position } from '@coin/shared/core/util-models';
import { Organisation } from '../../../api/generated';
import { LoadOrganisationTree } from '../../../org-management/state/org-management.actions';
import { OrgManagementState } from '../../../org-management/state/org-management.state';
import { LoadEmployeeProfile, LoadPositions } from './state/quickdraw-profile.actions';
import { QuickdrawProfileState } from './state/quickdraw-profile.state';
import { LoadingError } from '../../../org-management/models/loading-error.enum';

@Component({
  selector: 'cosmos-quickdraw-profile',
  templateUrl: './quickdraw-profile.component.html',
  styleUrls: ['./quickdraw-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class QuickdrawProfileComponent implements OnInit {
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  router = inject(Router);

  public loadingErrorType = LoadingError;

  @Input() public set employeeId(value: string) {
    this.employeeId$.next(value);
  }

  @Input() public set currentOrganisationId(value: string) {
    this.currentOrganisationId$.next(value);
  }

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public close = new EventEmitter<void>();

  public employeeId$ = new BehaviorSubject<string>(undefined);
  public currentOrganisationId$ = new BehaviorSubject<string>(undefined);

  public employeeDataLoading$: Observable<boolean> = this.store.select(QuickdrawProfileState.employeeProfileLoading);

  public employeeProfile$: Observable<EmployeeProfile> = this.employeeId$.pipe(
    tap(employeeId => {
      this.currentEmployeeId = employeeId;
    }),
    switchMap(id => this.store.select(QuickdrawProfileState.employeeProfile(id)))
  );

  public employeeProfileLoadingError$: Observable<LoadingError> = this.store.select(
    QuickdrawProfileState.employeeProfileLoadingError
  );

  public additionalPositions$: Observable<Position[]> = combineLatest([
    this.employeeId$,
    this.currentOrganisationId$
  ]).pipe(
    switchMap(([employeeId, currentOrganisationId]) =>
      this.store.select(QuickdrawProfileState.otherEmployeePositions(employeeId, currentOrganisationId))
    )
  );
  public currentPosition$: Observable<Position> = combineLatest([this.employeeId$, this.currentOrganisationId$]).pipe(
    switchMap(([employeeId, currentOrganisationId]) =>
      this.store.select(QuickdrawProfileState.employeePosition(employeeId, currentOrganisationId))
    )
  );
  public currentOrganisation$: Observable<Organisation> = this.currentOrganisationId$.pipe(
    switchMap(id => this.store.select(OrgManagementState.organisation(id)))
  );

  public showManagement$: Observable<boolean> = combineLatest([this.currentPosition$, this.currentOrganisation$]).pipe(
    map(
      ([currentPosition, currentOrganisation]) =>
        !this.isRootOrganisation(currentOrganisation) || !currentPosition?.isHead
    )
  );

  private currentEmployeeId: string;

  public ngOnInit(): void {
    this.employeeId$
      .pipe(
        filter(id => !!id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(id => this.loadEmployeeData(id));
    this.currentOrganisationId$
      .pipe(
        filter(id => !!id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(id => this.loadOrganisationData(id));
  }

  private loadEmployeeData(employeeId: string): void {
    this.store.dispatch(new LoadEmployeeProfile(employeeId));
    this.store.dispatch(new LoadPositions(employeeId));
  }

  private loadOrganisationData(organisationId: string): void {
    this.store.dispatch(new LoadOrganisationTree(organisationId));
  }

  private isRootOrganisation(orgData: Organisation): boolean {
    return !orgData?.parentOrganisationId;
  }

  public reload(): void {
    this.loadEmployeeData(this.currentEmployeeId);
  }
}
